import React, { useCallback, useEffect, useState } from 'react';
import {
    EventBannerContent,
    EventModalContent,
    isAnnouncement,
    resolveEventSurface,
    resolveThemePreset,
    type EventDto
} from '@odo/shared-ui';
import { resolveEventImageUrls } from '../../services/eventImages';
import EventBannerRsvp from './EventBannerRsvp';
import EventDetailModal from './EventDetailModal';

interface EventBannerProps {
    readonly event: EventDto;
    /** Rotation dots, rendered as a column against the banner's left edge.
     *  Absent when only one event is live, which is the common case and looks
     *  exactly as it did before rotation existed. */
    readonly controls?: React.ReactNode;
    /** Lets the strip hold rotation while the detail modal is open - advancing
     *  behind it would land the reader on a different event on close. */
    readonly onModalOpenChange?: (open: boolean) => void;
}

const EventBanner: React.FC<EventBannerProps> = ({ event, controls, onModalOpenChange }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const theme = resolveThemePreset(event.themePreset);
    const announcement = isAnnouncement(event);

    // Stable identity: EventBody's resolve effect re-runs whenever this
    // function changes, so an inline arrow would loop.
    const resolveImageUrls = useCallback((fileIds: string[]) => resolveEventImageUrls(fileIds), []);

    useEffect(() => {
        onModalOpenChange?.(isModalOpen);
    }, [isModalOpen, onModalOpenChange]);

    return (
        <>
            <div
                onClick={() => setIsModalOpen(true)}
                className={`${theme.bg} ${theme.bannerBorder} ${theme.text} relative w-full px-6 py-3 cursor-pointer hover:brightness-95 transition-all duration-150`}
            >
                {/* Anchored to the banner, not the content box inside its px-6:
                    that puts the dots close to the strip's real left edge, and
                    centres them on its full height instead of the content's. */}
                {controls && (
                    <div className="absolute inset-y-0 left-2 flex items-center">{controls}</div>
                )}

                <div className="relative">
                    <EventBannerContent
                        event={event}
                        reserveActionSpace={!announcement}
                        reserveLeadingSpace={Boolean(controls)}
                    />

                    {!announcement && (
                        <div className="absolute inset-y-0 right-0 flex items-center gap-3">
                            <EventBannerRsvp event={event} />
                        </div>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <EventDetailModal
                    title={resolveEventSurface(event, 'modal').title}
                    body={<EventModalContent event={event} resolveImageUrls={resolveImageUrls} />}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </>
    );
};

export default EventBanner;
